/*
 * Work order enums and their presentation.
 *
 * Labels live in features/cmms/cmmsMessages.js with the other enum label helpers so
 * they are translated; everything here is locale-independent. The badge and option
 * helpers therefore take an `intl` instance.
 */
import {
  getRelatedDocumentTypeLabel,
  getTaskStatusLabel,
  getWorkOrderPriorityLabel,
  getWorkOrderStatusLabel,
  getWorkOrderTypeLabel,
} from "../cmms/cmmsMessages";
// Safe to import: cmmsRoles is plain constants with no imports of its own, so this cannot
// recreate the cmmsMessages <-> enums cycle that broke the app on 2026-08-04.
import { CMMS_MANAGER_ROLES, CMMS_TECHNICIAN_ROLES } from "../cmms/cmmsRoles";

export const WorkOrderStatus = {
  Draft: 1,
  Open: 2,
  InProgress: 3,
  Completed: 4,
  Closed: 5,
  Escalated: 6,
};

/*
 * The status enum backwards.
 *
 * /workorders/mine parses status *names* — Enum.TryParse over a comma-separated list — while
 * everything on the client side of the page holds the numbers above. This is the one place that
 * needs the mapping, and reading it off the enum keeps the two from drifting apart.
 */
export const getWorkOrderStatusName = (status) =>
  Object.keys(WorkOrderStatus).find((name) => WorkOrderStatus[name] === status) ?? null;

export const WorkOrderType = {
  MachineBreakdown: 1,
  MachineProblem: 2,
  PreventiveMaintenance: 3,
};

export const WorkOrderPriority = {
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4,
};

export const WorkOrderTaskStatus = {
  Open: 1,
  InProgress: 2,
  Completed: 3,
};

export const DocumentType = {
  PmTemplate: 1,
  Sop: 2,
  Drawing: 3,
  Manual: 4,
  Other: 5,
};

/*
 * What "open" means, for everything in the app that counts open work orders.
 *
 * Lifted out of machineDetails/components/MachineWorkOrdersTabLabel.jsx, where it had been a
 * private const. Defined once here because the sidebar badge, the machine tab badge and the
 * priority banner all answer the same question, and two of them disagreeing would put two
 * numbers on screen for one reality.
 *
 * It follows the backend rather than inventing: WorkOrderService.GetMineAsync defaults to these
 * same two statuses, and that default is what fills the sidebar.
 *
 * Escalated is excluded because nothing produces it any more. EscalateAsync moves the one work
 * order to the receiving department and leaves it Open, so an escalated fault is already counted
 * here as the Open order it is. The status survives only on rows written before that change, and
 * those are stuck in a state nobody is working — which is not an open job either. Draft is
 * excluded because a fault not yet raised is nobody's queue.
 */
export const OPEN_WORK_ORDER_STATUSES = [WorkOrderStatus.Open, WorkOrderStatus.InProgress];

export const isWorkOrderOpen = (status) => OPEN_WORK_ORDER_STATUSES.includes(status);

const workOrderStatusValues = [
  WorkOrderStatus.Draft,
  WorkOrderStatus.Open,
  WorkOrderStatus.InProgress,
  WorkOrderStatus.Completed,
  WorkOrderStatus.Closed,
  WorkOrderStatus.Escalated,
];

const workOrderTypeValues = [
  WorkOrderType.MachineBreakdown,
  WorkOrderType.MachineProblem,
  WorkOrderType.PreventiveMaintenance,
];

const workOrderPriorityValues = [
  WorkOrderPriority.Low,
  WorkOrderPriority.Medium,
  WorkOrderPriority.High,
  WorkOrderPriority.Critical,
];

const workOrderTaskStatusValues = [
  WorkOrderTaskStatus.Open,
  WorkOrderTaskStatus.InProgress,
  WorkOrderTaskStatus.Completed,
];

const documentTypeValues = [
  DocumentType.PmTemplate,
  DocumentType.Sop,
  DocumentType.Drawing,
  DocumentType.Manual,
  DocumentType.Other,
];

const toOptions = (values, labelFn, intl) =>
  values.map((value) => ({ id: value, value, title: labelFn(value, intl), label: labelFn(value, intl) }));

export const getWorkOrderStatusOptions = (intl) => toOptions(workOrderStatusValues, getWorkOrderStatusLabel, intl);

/*
 * Status options the given roles may actually request from `currentStatus`.
 *
 * Mirrors the tiering the controller enforces in ChangeStatus:
 *   Closed, from Completed    technician or manager — finishing the job you did
 *   Closed, from anywhere     manager only — force-close, skips the work
 *   InProgress / Completed    technician or manager
 *   everything else           any field user — raising a fault is the operator's job
 *
 * Closed is the one option whose answer depends on the edge rather than the target, and
 * that is the point: closing is the only transition that writes a ToolServiceLog, so a
 * technician who cannot close can never record the service they performed. Force-closing
 * from Open writes the same record with no work behind it, which is why it stays manager.
 *
 * `currentStatus` is optional, and an unknown one falls back to manager-only. That is the
 * safe direction: offering a status the backend will Forbid is worse than withholding one
 * it would have allowed.
 *
 * Escalated is never offered as something to change *to*. Escalating is not a status change —
 * it moves the work order to another department and leaves it Open — so the backend refuses every
 * transition into it, and offering it here would be a control that can only produce a 400. It is
 * kept in the list when the order is already in it, which happens only for rows written before
 * that change: the Select needs the current value present or it renders blank, and Escalated used
 * to be terminal, so that dropdown is the only way those get back into a queue.
 *
 * Filtering rather than hiding the whole control: an operator still needs Draft -> Open.
 */
export const getPermittedWorkOrderStatusOptions = (intl, roleIds, currentStatus) => {
  const has = (allowed) => (roleIds ?? []).some((id) => allowed.includes(id));
  const isManager = has(CMMS_MANAGER_ROLES);
  const isTechnician = has(CMMS_TECHNICIAN_ROLES);

  return getWorkOrderStatusOptions(intl).filter((option) => {
    if (option.value === WorkOrderStatus.Escalated) {
      return currentStatus === WorkOrderStatus.Escalated;
    }
    if (option.value === WorkOrderStatus.Closed) {
      return currentStatus === WorkOrderStatus.Completed ? isTechnician : isManager;
    }
    if (option.value === WorkOrderStatus.InProgress || option.value === WorkOrderStatus.Completed) {
      return isTechnician;
    }
    return true;
  });
};
export const getWorkOrderTypeOptions = (intl) => toOptions(workOrderTypeValues, getWorkOrderTypeLabel, intl);
export const getWorkOrderPriorityOptions = (intl) => toOptions(workOrderPriorityValues, getWorkOrderPriorityLabel, intl);
export const getWorkOrderTaskStatusOptions = (intl) => toOptions(workOrderTaskStatusValues, getTaskStatusLabel, intl);
export const getDocumentTypeOptions = (intl) => toOptions(documentTypeValues, getRelatedDocumentTypeLabel, intl);

const statusPalette = {
  [WorkOrderStatus.Draft]: { bg: "#9e9e9e", text: "#fff" },
  [WorkOrderStatus.Open]: { bg: "#1976d2", text: "#fff" },
  [WorkOrderStatus.InProgress]: { bg: "#7b1fa2", text: "#fff" },
  [WorkOrderStatus.Completed]: { bg: "#388e3c", text: "#fff" },
  [WorkOrderStatus.Closed]: { bg: "#263238", text: "#fff" },
  [WorkOrderStatus.Escalated]: { bg: "#f57c00", text: "#fff" },
};

const typePalette = {
  [WorkOrderType.MachineBreakdown]: { bg: "#d32f2f", text: "#fff" },
  [WorkOrderType.MachineProblem]: { bg: "#f57c00", text: "#fff" },
  [WorkOrderType.PreventiveMaintenance]: { bg: "#1976d2", text: "#fff" },
};

const priorityPalette = {
  [WorkOrderPriority.Low]: { bg: "#9e9e9e", text: "#fff" },
  [WorkOrderPriority.Medium]: { bg: "#fbc02d", text: "#000" },
  [WorkOrderPriority.High]: { bg: "#f57c00", text: "#fff" },
  [WorkOrderPriority.Critical]: { bg: "#d32f2f", text: "#fff" },
};

const taskStatusPalette = {
  [WorkOrderTaskStatus.Open]: { bg: "#1976d2", text: "#fff" },
  [WorkOrderTaskStatus.InProgress]: { bg: "#7b1fa2", text: "#fff" },
  [WorkOrderTaskStatus.Completed]: { bg: "#388e3c", text: "#fff" },
};

const documentTypePalette = {
  [DocumentType.PmTemplate]: { bg: "#1976d2", text: "#fff" },
  [DocumentType.Sop]: { bg: "#388e3c", text: "#fff" },
  [DocumentType.Drawing]: { bg: "#7b1fa2", text: "#fff" },
  [DocumentType.Manual]: { bg: "#f57c00", text: "#fff" },
  [DocumentType.Other]: { bg: "#9e9e9e", text: "#fff" },
};

const fallback = { bg: "#9e9e9e", text: "#fff" };

export const getStatusBadge = (status, intl) => ({
  label: getWorkOrderStatusLabel(status, intl),
  ...(statusPalette[status] ?? fallback),
});

export const getTypeBadge = (type, intl) => ({
  label: getWorkOrderTypeLabel(type, intl),
  ...(typePalette[type] ?? fallback),
});

export const getPriorityBadge = (priority, intl) => ({
  label: getWorkOrderPriorityLabel(priority, intl),
  ...(priorityPalette[priority] ?? fallback),
});

export const getTaskStatusBadge = (status, intl) => ({
  label: getTaskStatusLabel(status, intl),
  ...(taskStatusPalette[status] ?? fallback),
});

export const getDocumentTypeBadge = (type, intl) => ({
  label: getRelatedDocumentTypeLabel(type, intl),
  ...(documentTypePalette[type] ?? fallback),
});
